import * as angular from 'angular';
import { INavEditContext, IViewPageDisplay, IViewProperty, IViewPropertyGroup, NavPropertyItem, NavPropertyValues } from '@common/navdata';
import { NavKendoRegister } from '@common/navkendo';
import { Bind, Bind as kBind, constants as kconstants, CreateFormula } from '@common/navkendo/kutils';
import { NavItemsGrid } from '@common/navkendo/navitemsgrid';
import { INavPageItemsControl, INavPageItemEditOptions } from '@common/navkendo/navpageitems';
import { InfUserSettingsAccess, IPersistentSetting, NavSettingsService } from '@common/services/settingsservice';
import { fn } from '@common/utils';
import { INavDockZonesOptions, INavDockZonesSetting, NavDockZones } from '../navkendo/navdock';
import UISafeAction = fn.UISafeAction;
import ToDictionary = fn.ToDictionary;

/** Custom DockZones Implementation for PageItemEditor */
export class NavPageitemDockZones extends NavDockZones implements INavPageItemsControl, INavEditContext {
    public options: INavPageitemDockZonesOptions;
    constructor(element, options: INavPageitemDockZonesOptions) {
        super(element, options);
        this.element.data('INavEditContext', this);
        this.element.data('kendoNavDockZones', this); // due to inheritance
        this.refreshStructure(options);
    }

    destroy() {
        // WTF -> see kendo.angular.js: 'propagateClassToWidgetWrapper'
        this.trigger("destroy");
        // Imp. resets Events, stores settings etc
        this.refreshStructure();
        delete this.userSettings;
        this.element.removeData('INavEditContext');
        this.element.removeData('kendoNavDockZones');
        super.destroy();
    }

    /** Add Settings By Name, If Settings loaded apply them also */
    AddSetting<T>(name: string, s: IPersistentSetting<T>) {
        if (!this.userSettings)
            return;
        return this.userSettings.Add(name, s);
    }

    AddDispose(disposeCallback: () => void) {
        this.bind(kconstants.NavDestroy, disposeCallback);
    }

    GetAttachedParent(): JQuery {
        return this.element;
    }

    private get NavSettings() {
        var result = this.options.NavSettings;
        if (!result) {
            var injector = <ng.auto.IInjectorService>angular.element(this.element).injector();
            result = injector && injector.get<NavSettingsService>("NavSettings");
        }
        return result;
    }
    /** current Settings object (changes with change of currentPersistkey */
    private userSettings: InfUserSettingsAccess;
    /** TODO: for "Custom Wizard View .... will apply twice: is a problem here ... */
    private userSettingsApplied: boolean = false;
    /** Current Key-Path for Loading/Saving UserSettings */
    private currentPersistKey: string;
    private currentPageItemOptions: INavPageItemEditOptions;
    private currentItem: NavPropertyItem;
    /** (re) Init (or cleanup : options empty) dynamic UI Docks from new Structure */
    private refreshStructure(options?: INavPageItemEditOptions) {
        options = options || { Item: undefined, View: undefined };

        // trigger cleanup of last init
        this.trigger(EVENT_REFRESHSTRUCTURE);

        if (options.Item !== undefined && options.View) {
            this.one(EVENT_REFRESHSTRUCTURE, () => {
                if (this.userSettings) {
                    this.userSettings.Save();
                }
            });
        }

        // Setup next init reset on External Settings inject
        if (options.Register && $.isFunction(options.Register)) {
            var prevChangeTracker = options && options.View && options.View.ChangeTracker;
            this.one(EVENT_REFRESHSTRUCTURE, options.Register(newOptions => {
                var currOptions = this.currentPageItemOptions || <INavPageItemEditOptions>{ Item: undefined, View: {} },
                    newChangeTracker = newOptions.View && newOptions.View.ChangeTracker,
                    sameView = newOptions.View === currOptions.View && (prevChangeTracker === newChangeTracker),
                    sameMeta = (newOptions.Item && newOptions.Item.MetaData) === (currOptions.Item && currOptions.Item.MetaData);
                if (sameView && (sameMeta || !newOptions.Item)) {
                    this.currentItem = newOptions.Item;
                    this.trigger(EVENT_SETCURRENT);
                } else {
                    // set item in refresh : do not refresh UI Comonents that will be deleted anyway
                    this.refreshStructure(newOptions);
                }
            }));
        }

        this.currentPageItemOptions = options;

        if (!options.View || !options.Item == undefined) {
            return;
        }

        let view = options.View,
            initWithSettings = fn.proxy(this.initWithSettings, this),
            pToken = view && view.UserPersistToken;

        this.userSettingsApplied = false;
        if (pToken && pToken.length) {
            if (this.currentPersistKey != (this.currentPersistKey = pToken.slice().join("/"))) {
                if (this.userSettings)
                    this.userSettings.RefreshPersistKey(this.currentPersistKey, true);
                else {
                    var navSettings = this.NavSettings;
                    if (navSettings) {
                        this.userSettings = navSettings.CreateUserSettings(this.currentPersistKey);
                       this.userSettings.Add("DockLayout", fn.proxy(super.GetSettings, this), s => {
                            if (!this.userSettingsApplied) {
                                this.userSettingsApplied = true;
                                this.initWithSettings(s);
                            }
                        });
                    }
                }
            }
        } else {
            delete this.currentPersistKey;
        }

        var isInitialized = false;
        this.one(EVENT_INIT, () => { isInitialized = true; });
        /** defer initialization after loaded usersettings if applicaple */
        if (this.userSettings) {
            const afterLoad = () => {
                // wOut Stored Settings apply does not get Called
                if (!isInitialized)
                    initWithSettings();
            };
            this.userSettings.Load().then(afterLoad, afterLoad);
        } else {
            initWithSettings();
        }
    }
    private initWithSettings(zonesSettings?: INavDockZonesSetting) {
        this.trigger(EVENT_INIT);
        var options = this.currentPageItemOptions;
        /** Prefix for Dynamic Dock Zones */
        var dynPrefix = "NavDyn_";
        UISafeAction(() => {
            var item = this.currentItem = options.Item,
                metaData = options.Item && options.Item.MetaData,
                view = options.View,
                settings = zonesSettings && zonesSettings.DockSettings,
                sInfo = ToDictionary(settings || [], "ID");

            this.trigger(EVENT_SETCURRENT);
            var me = this;
            fn.Remap(sInfo, (value, key) => {
                if (key && !(key.substring(0, dynPrefix.length) == dynPrefix)) {
                    var dock = me.GetDockByID(key);
                    if (dock && value && value.Layout) {
                        if (value.Layout.IsCollapsed)
                            dock.Collapse();
                        else
                            dock.Expand(false);
                    }
                }
            });

            if (view && view.Groups) {
                /** Static Doc Information for Items with User Stored Settings */
                var applyStaticItems = Object.getOwnPropertyNames(sInfo).map(n => {
                    if (n.indexOf(dynPrefix) < 0) {
                        var storedLayout = sInfo[n];
                        var xDock = this.GetDockByID(n);
                        if (xDock && storedLayout.Layout) {

                            return {
                                ID: n,
                                DockZone: storedLayout.Layout.DockZone,
                                Position: storedLayout.Layout.Position,
                                Dynamic: false,
                                Apply: () => {
                                    xDock.ApplySettings(sInfo[n].Layout, false);
                                    return xDock;
                                }
                            };
                        }
                    }
                }).filter(fn.isSet);
                /** Dynamic Doc Information */
                if (metaData) {
                    var propertyValues = metaData && new NavPropertyValues(metaData, item);
                    this.one(kconstants.NavDestroy, () => propertyValues.destroy());
                }
                var applyDynamicItems = view.Groups.map(viewConfig => {
                    viewConfig = viewConfig || <IViewPropertyGroup>{};
                    let viewProps = viewConfig.Properties,
                        singleProp = viewProps && viewProps.length == 1 && metaData && metaData.PropsByName[viewProps[0].Name],
                        /** Content is single Grid Item */
                        isSingleArrProp = singleProp && singleProp.ArrayProperties,
                        isSingleGrid = isSingleArrProp && isSingleArrProp.LineDisplayMode !== "ForceGroup";

                    let layout = {
                        CanResize: !isSingleGrid, // Single Grid : Fit Size to Content
                        DockZone: 0,
                        Height: isSingleGrid ? 'auto' : undefined,
                        IsCollapsed: !!viewConfig.InitialCollapsed,
                        Position: 0,
                        MaxContentHeightConstraint: !isSingleGrid
                    };

                    var id = dynPrefix + viewConfig.ID;
                    var storedLayout = sInfo[id];
                    if (storedLayout && storedLayout.Layout) {
                        var sLayout = storedLayout.Layout;
                        layout.IsCollapsed = sLayout.IsCollapsed;
                        layout.Position = sLayout.Position;
                        layout.DockZone = sLayout.DockZone;
                    }

                    return {
                        ID: id,
                        DockZone: layout.DockZone,
                        Position: layout.Position,
                        Dynamic: true,
                        Apply: () => {
                            var title = viewConfig.Title || "<??>";
                            var result = super.AddDock({
                                ID: id,
                                Title: title,
                                Layout: layout
                            });
                            result.element.data("IViewPropertyGroup", viewConfig); // to retrieve ...
                            result.bind(kconstants.ExpansionStateChanged, () => me.trigger("layoutChange"));
                            var propGroup = $("<div></div>");
                            let propagated = viewConfig.PropagateProperties;
                            if (propagated && propagated.length > 0) {
                                let propParent = result.element.find(".idms_DockWidgetCommands");
                                propagated.forEach((pViewItem, idx) => {
                                    let display = $("<span></span>"),
                                        pItem = pViewItem.Name,
                                        xProp = metaData && metaData.PropsByName[pItem],
                                        onChange = (show?: boolean) => {
                                            if (typeof show == "boolean") {
                                                display.toggle(show);
                                            }
                                            let curr = this.currentItem,
                                                dValue = curr ? curr.GetDisplayValue(pItem) : "";
                                            display.text(dValue + (idx > 0 ? " | " : " "));
                                            xProp = xProp || (curr && curr.MetaData.PropsByName[pItem]);
                                            var title = pViewItem.Title || (xProp && xProp.Title);
                                            if (title)
                                                display.attr("title", title + ": " + dValue);
                                        }, onItemChange = () => {
                                            onChange(result.IsCollapsed());
                                            if (this.currentItem) {
                                                this.one(EVENT_SETCURRENT, this.currentItem.RegisterPropertyChanged(xProp, () => onChange()));
                                            }
                                        };
                                    display.prependTo(propParent);

                                    // adjust on Current Item Change
                                    this.one(EVENT_REFRESHSTRUCTURE, kBind(this, onItemChange, EVENT_SETCURRENT));
                                    onItemChange();
                                    // adjust on expansion State Changed
                                    this.one(EVENT_REFRESHSTRUCTURE, kBind(result, () => onChange(result.IsCollapsed()), kconstants.ExpansionStateChanged));

                                });
                            }
                            result.ContentContainer.append(propGroup);
                            var kPropGroup = propGroup.kendoNavPageItemPropertyGroup({ item: propertyValues || item, view: viewConfig, DesignMode: options && options.DesignMode }).data("kendoNavPageItemPropertyGroup");
                            // Ensure Proper Destruction on Parents Destruction
                            result.one(kconstants.NavDestroy, () => kPropGroup.destroy());
                            // Dock with single grid : Refresh Dock Count Info by grid Count Info
                            var kSingleGrid: kendo.ui.Grid;
                            if (isSingleGrid && (kSingleGrid = propGroup.find(":data('kendoGrid')").data("kendoGrid"))) {

                                // Update on change
                                let getSelInfo: () => string = fn.noop;
                                const SetCountByGrid = () => { result.SetCountByGrid(kSingleGrid, getSelInfo()); };
                                kSingleGrid.bind("dataBound", SetCountByGrid);
                                if (kSingleGrid instanceof NavItemsGrid) {
                                    const kItemsGrid = kSingleGrid;
                                    getSelInfo = () => kItemsGrid.CurrentItems && kItemsGrid.CurrentItems.SelectionInfo || "";
                                    const gridItemsChanged = () => {
                                        var items = kItemsGrid.CurrentItems;
                                        if (items) {
                                            kItemsGrid.one("SetItems", Bind(items, SetCountByGrid, "selectionchanged"));
                                        }
                                        kItemsGrid.one("SetItems", gridItemsChanged);
                                    };
                                    gridItemsChanged();
                                }
                                // Initial update
                                SetCountByGrid();

                                // Setup InvalidCounter Changed Notification
                                if (kSingleGrid) {
                                    var refreshAlertFromGrid = () => {
                                        var count = <number>kSingleGrid["InvalidLinesCount"] || 0;
                                        if (count == 1)
                                            title = "1 Zeile enthält fehlende Pflichtfelder";
                                        else if (count > 1)
                                            title = count + " Zeilen enthalten fehlende Pflichtfelder";
                                        result.UpdateAlertCount(count, title || "");
                                    };
                                    kSingleGrid.bind("InvalidLinesCountChanged", refreshAlertFromGrid);
                                    refreshAlertFromGrid();
                                    // MSQQQ
                                    //var tester = true;
                                    //if (tester) {
                                    //    let prop = item.MetaData.PropsByName[viewConfig.Properties[0].Name],
                                    //        testProps = prop.ArrayProperties && prop.ArrayProperties.Properties.map(item => prop.ArrayProperties.PropsByName[item.Name]).filter(fn.isSet).filter(item => item.GridVisibility == NavGridVisibility.InitialVisible || (item.GridVisibility == NavGridVisibility.Default && item.Invisible === false)),
                                    //        lineInfo = item.Root.GetLineData(prop),
                                    //        initItem = lineInfo.Items[0];
                                    //    var t = $("<div></div>");

                                    //    result.ContentContainer.append(t);

                                    //    var testElement = $(t).kendoNavPageItemPropertyGroup({
                                    //        Item: initItem || lineInfo.MetaData, View: {
                                    //            ID: "qqq", Title: "test", Properties: testProps.map(p => {
                                    //                return {
                                    //                    Name: p.Name,
                                    //                }
                                    //            })
                                    //        }
                                    //    });
                                    //    var testGroup = testElement.data("kendoNavPageItemPropertyGroup");
                                    //    result.one(kconstants.NavDestroy, lineInfo.RegisterSelectionChanged(() => {
                                    //        testGroup.SetCurrentItem(lineInfo.SelectionCount == 1 && lineInfo.SelectedItems[0]);
                                    //    }));
                                    //}
                                }
                            } else if (isSingleArrProp) {
                                // Single Array Prop, not rendered as Grid : Show Count in Header
                                var lineData = item.Root.GetLineData(singleProp);
                                if (lineData) {
                                    this.bind(kconstants.NavDestroy, lineData.RegisterCountChanged(() => result.SetCount(lineData.Count)));
                                }
                            } else {
                                var refreshAlertFromGroup = () => {
                                    var title = kPropGroup.InvalidCount == 1 ?
                                        kPropGroup.InvalidCount + " fehlendes Pflichtfeld" : kPropGroup.InvalidCount + " fehlende Pflichtfelder";
                                    result.UpdateAlertCount(kPropGroup.InvalidCount, title);
                                };
                                // Seup InvalidCounter Changed Notification
                                kPropGroup.bind("InvalidCountChanged", refreshAlertFromGroup);
                                refreshAlertFromGroup();
                            }

                            // Per Group BusyIndicator
                            if (options.Item && result && result.ContentContainer) {
                                me.one(EVENT_REFRESHSTRUCTURE,
                                    options.Item.Root.Anchor.RegisterUpdateStateChanged(state => result.ContentContainer.kendoNavBusyIndicator(state && state.Update)));
                            }

                            if (result && viewConfig && viewConfig.VisibilityFormula) {
                                var refresh = CreateFormula({
                                    formula: viewConfig.VisibilityFormula,
                                    onChange: (val) => {
                                        result.element.toggle(!!(val || options.DesignMode));
                                    },
                                    onError: err => { try { console.error(err); } catch (xErr) { }; return false; }
                                });

                                // SetUp Reaction on : CurrentItems Property Changed, Current Item Changes, Structure Change
                                let releaseLast = fn.noop;
                                var onCurrentChange = () => {
                                    releaseLast();
                                    releaseLast = fn.noop;
                                    var currentItem = this.currentItem;
                                    if (currentItem) {
                                        var doIt = () => refresh(currentItem.Model);
                                        refresh(currentItem.Model);
                                        releaseLast = currentItem.RegisterPropertyChanged(null, doIt);
                                    }
                                };
                                me.bind(EVENT_SETCURRENT, onCurrentChange);
                                me.one(EVENT_REFRESHSTRUCTURE, () => {
                                    releaseLast();
                                    releaseLast = fn.noop;
                                    me.unbind(EVENT_SETCURRENT, onCurrentChange);
                                });
                                onCurrentChange();
                            }
                            return result;
                        }
                    };

                }, this);

                // Sort all By DockZone and Position : Applying them in natural order makes settings adjustment easier!
                var allSortedDockItems = applyDynamicItems.slice();
                allSortedDockItems.push(...applyStaticItems);
                allSortedDockItems = allSortedDockItems.sort((a, b) => (a.DockZone - b.DockZone) || (a.Position - b.Position));

                const GroupRendered = this.options.GroupRendered || fn.noop;
                allSortedDockItems.forEach(item => {
                    var doc = item.Apply();
                    if (item.Dynamic) {
                        GroupRendered(doc.element);
                        // Tear Down Created Dynamic Dock on next Init /Reset Cycle
                        me.one(EVENT_REFRESHSTRUCTURE, () => {
                            var element = doc.element;
                            doc.destroy();
                            element.remove();
                        });
                    }
                });

                // SetUp Dispatch of Current Item to UI until next structure Change
                var applyCurrentItem = () => propertyValues && propertyValues.SetCurrentItem(this.currentItem);
                this.one(EVENT_REFRESHSTRUCTURE, kBind(this, applyCurrentItem, EVENT_SETCURRENT));

                // Apply splitter pane widths
                if (zonesSettings && zonesSettings.DockZoneWidths) {
                    this.ApplySplitterSettings(zonesSettings.DockZoneWidths);
                }
                // Backward Notification about finished Render Action
                if (options.Rendered && $.isFunction(options.Rendered))
                    options.Rendered(this.element);
            };
        }, true);
    }

    /** Get Options Clone for Current State, representing :
     * - order
    /* - width, explicit title
     */
    public getCurrentView(): IViewPageDisplay {

        var result = fn.clone(this.currentPageItemOptions && this.currentPageItemOptions.View);
        if (!result)
            return result;

        // Reorder Groups from UI
        result.Groups = this.GetKendoDocks().map(d => {
            var source = fn.clone(<IViewPropertyGroup>d.element.data("IViewPropertyGroup"));
            if (source) {
                // MSQQQ : really???
                if (source.Properties.length == 1) {
                    var sProp = source.Properties[0];
                    var grid = $("[navPropertyName='" + sProp.Name + "']", d.element).data("kendoNavGrid");
                    if (grid && grid.getAllColumns) {
                        var sPropMaps = ToDictionary(sProp.Details || [], "Name");
                        sProp.Details = grid.getAllColumns().filter(g => !!g.field).map(item => {
                            var sPropMap = sPropMaps[item.field];
                            return <IViewProperty>{
                                Name: item.field,
                                width: item.width,
                                hidden: item.hidden,
                                Title: (sPropMap && sPropMap.Title) || undefined
                            };
                        });
                    }
                };
                return source;
            }
        }).filter(fn.isSet);

        return result;
    }
}
NavKendoRegister(NavPageitemDockZones, "NavPageitemDockZones");

/** Merged Options for Base DockZones, NavÜageItemEditorOptios */
export interface INavPageitemDockZonesOptions extends INavDockZonesOptions, INavPageItemEditOptions {
    /* not necessray in angular scenario */
    NavSettings?: NavSettingsService;
}

/** Event fired on PageItem DockZones when the View/PageMeta Info for Display Changed */
const EVENT_REFRESHSTRUCTURE = "pageItemInit";
/** Event fired on PageItem DockZones when the Current Item Change on Same View */
const EVENT_SETCURRENT = "setCurrentItem";
const EVENT_INIT = "initWithSettings";

declare global {
    interface JQuery {
        /** NavDockZones Extension for NavpPageItem Display */
        kendoNavPageitemDockZones(options: INavPageitemDockZonesOptions);
        data(key: "kendoNavPageitemDockZones"): NavPageitemDockZones;
    }
}
